'use client';

import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';

import phoneCountries from '@monoimin/datasources/phone-countries';

import { usePhoneDigits } from '@monoimin/hooks';
import { PhoneCountry } from '@monoimin/sb-types';
import { getIpInfoCached } from '@monoimin/utils/analytics';

import {
  makeSureStartWithPlusOrEmpty,
  getInputGrid,
} from '@monoimin/utils/helpers';
import { gravityFormsDict } from '@monoimin/intl';

import { PhoneCodesSelect } from './PhoneCodesSelect';

import type { InputProps } from '@monoimin/forms/gravity-form/types';

export const PhoneInput = ({
  customId,
  adminLabel,
  locale,
  layoutGridColumnSpan = 12,
  classNames,
}: InputProps) => {
  const dict = gravityFormsDict[locale];

  const containerRef = useRef<HTMLDivElement>(null);

  const [showValidationMessage, setShowValidationMessage] = useState(false);

  const { data: ipInfoData, isLoading: isIpInfoLoading } = useSWR(
    {},
    getIpInfoCached
  );

  const [defaultPhoneCountry, setDefaultPhoneCountry] =
    useState<PhoneCountry | null>(null);

  const {
    phone,
    isValid,
    setPhone,
    handlePhoneChange,
    phoneCountryCode,
    setPhoneCountryCode,
  } = usePhoneDigits({ defaultPhoneCountry });

  const phoneCountry = useMemo(() => {
    return phoneCountries.find((item) => {
      return item.countryCode === phoneCountryCode;
    });
  }, [phoneCountryCode]);

  const onSelectChange = useCallback(
    async (value: PhoneCountry) => {
      const { callingCode, countryCode } = value;

      setPhoneCountryCode(countryCode);

      setPhone(makeSureStartWithPlusOrEmpty(callingCode));
    },
    [setPhone, setPhoneCountryCode]
  );

  const gridColumn = getInputGrid(layoutGridColumnSpan);

  useEffect(() => {
    if (!ipInfoData?.ipCountryCode) {
      return;
    }

    const ipPhoneCountry = phoneCountries.find((item) => {
      return item.countryCode === ipInfoData.ipCountryCode;
    });

    if (ipPhoneCountry) {
      onSelectChange(ipPhoneCountry);
      setDefaultPhoneCountry(ipPhoneCountry);
    }
  }, [ipInfoData, setPhone, onSelectChange]);

  return (
    <div
      ref={containerRef}
      className={clsx('mb-3', classNames?.input, gridColumn)}
      data-testid='phone-input'
    >
      <div className='relative flex items-center w-full h-15 md:h-16 bg-white border border-line px-3'>
        {isIpInfoLoading ? (
          <div
            data-testid='phone-codes-skeleton'
            className='w-10 h-6 skeleton'
          />
        ) : (
          <PhoneCodesSelect
            selectedValue={phoneCountry || null}
            setSelectedValue={onSelectChange}
            phoneCountries={phoneCountries}
            classNames={{
              options: '-translate-x-3',
            }}
            locale={locale}
          />
        )}
        <div className='flex-1'>
          <input
            data-testid='phone-input-control'
            id={customId}
            value={phone}
            required
            name={adminLabel || customId}
            disabled={isIpInfoLoading}
            type='tel'
            autoComplete='tel'
            onBlur={() => {
              if (isValid) {
                return;
              }

              setShowValidationMessage(true);
            }}
            className='w-full h-full py-4 px-2 appearance-none focus:outline-0 text-xl'
            onChange={(event) => {
              handlePhoneChange(event);
              setShowValidationMessage(false);
            }}
          />
        </div>
      </div>
      {showValidationMessage && (
        <p
          data-testid='phone-input-error'
          className='text-caption text-thirdlyErrors mt-2'
        >
          {dict.enterValidPhone}
        </p>
      )}
    </div>
  );
};
